import { useGetProjectCobrandingQuery } from '@/generated/graphql';
import { createContext, ReactNode, useContext, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { parseDirectInvitationToken } from '@/utils/auth';
import { useHideFaviconWhenCoBranded } from '@/hooks/useHideFaviconWhenCoBranded';
import { CO_BRANDING } from '@/constants/authentication';

export interface AuthBranding {
  companyLogoUrl?: string | null;
  companyName?: string | null;
  name?: string | null;
  accentColor1?: string | null;
}

interface BrandingContextValue {
  branding: AuthBranding | null;
}

type StoredCoBranding = {
  projectSubId: string;
  branding: AuthBranding;
  expiresAt: number;
};

const CO_BRANDING_TTL_MS = 24 * 60 * 60 * 1000;

function getStoredCoBranding(): AuthBranding | null {
  const raw = localStorage.getItem(CO_BRANDING);
  if (!raw) {
    return null;
  }

  try {
    const stored = JSON.parse(raw) as StoredCoBranding;

    if (stored.expiresAt < Date.now()) {
      localStorage.removeItem(CO_BRANDING);
      return null;
    }

    return stored.branding;
  } catch {
    localStorage.removeItem(CO_BRANDING);
    return null;
  }
}

/**
 * Company branding for the auth screens, set by views that resolve a
 * project's co-branding (direct candidate invites to co-branded or
 * candidate-portal roles). The layout consumes it: company logo in the
 * header, accent colour on the primary buttons. Null = default SZ styling.
 */
const BrandingContext = createContext<BrandingContextValue>({
  branding: null,
});

export function BrandingProvider({ children }: { children: ReactNode }) {
  const [searchParams] = useSearchParams();
  const invitationToken = searchParams.get('direct-inv');
  const projectSubIdFromUrl = searchParams.get('projectSubId');
  const { projectSubId: projectSubIdFromToken } =
    parseDirectInvitationToken(invitationToken);
  const projectSubId =
    projectSubIdFromUrl || projectSubIdFromToken || undefined;

  const { data, loading } = useGetProjectCobrandingQuery({
    variables: { subId: projectSubId || '' },
    skip: !projectSubId,
  });

  // Whenever a projectSubId resolves a fresh query, this URL-provided value
  // takes precedence over whatever was previously cached: a found
  // co-branding replaces the cache, a confirmed absence of co-branding for
  // this project clears it, so a candidate who moves on to another company's
  // invite doesn't carry the previous one's branding past this point.
  useEffect(() => {
    if (!projectSubId || loading) {
      return;
    }

    if (data?.getProjectCobranding) {
      const stored: StoredCoBranding = {
        projectSubId,
        branding: data.getProjectCobranding,
        expiresAt: Date.now() + CO_BRANDING_TTL_MS,
      };
      localStorage.setItem(CO_BRANDING, JSON.stringify(stored));
    } else {
      localStorage.removeItem(CO_BRANDING);
    }
  }, [projectSubId, loading, data]);

  // No projectSubId in the current URL (e.g. a revisit or a post-logout
  // redirect) falls back to the last co-branding seen on this device, as
  // long as it hasn't expired.
  const branding = projectSubId
    ? (data?.getProjectCobranding ?? null)
    : getStoredCoBranding();

  useHideFaviconWhenCoBranded(!!branding);

  const value = { branding };

  return (
    <BrandingContext.Provider value={value}>
      {loading ? null : children}
    </BrandingContext.Provider>
  );
}

export const useAuthBranding = () => useContext(BrandingContext);
